import React from 'react';
import { Plus } from 'lucide-react';
import { BlockChip, InstallWindowChip, MarketingCallChip } from './BlockChip';
import { WeatherChip } from './WeatherChip';
import { addDays, coversDay, iso, startOfWeek, todayIso } from './useSchedule';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function DayCell({ dayIso, inMonth, blocks, installs, calls = [], isStaff, projects, crew, readOnly, compact, weather, rainRisks, onAdd, onEdit, onShift, onDuplicate }) {
  const isToday = dayIso === todayIso();
  const d = new Date(dayIso);
  return (
    <div className={`min-h-[110px] border-b border-r border-gray-200 p-1 flex flex-col gap-1 ${inMonth ? 'bg-white' : 'bg-gray-50/70'} ${isToday ? 'ring-2 ring-inset ring-[#006400]' : ''}`} data-testid={`day-${dayIso}`}>
      <div className="flex items-center justify-between gap-1">
        <span className={`text-xs font-bold ${isToday ? 'bg-[#006400] text-white rounded-full w-6 h-6 inline-flex items-center justify-center' : inMonth ? 'text-gray-800' : 'text-gray-400'}`}>{d.getDate()}</span>
        {weather && <WeatherChip w={weather} compact={compact} />}
        {!readOnly && (
          <button type="button" onClick={() => onAdd(dayIso)} className="p-0.5 rounded text-gray-300 hover:text-[#006400] hover:bg-[#006400]/10" aria-label="Add block" data-testid={`add-${dayIso}`}><Plus className="w-3.5 h-3.5" /></button>
        )}
      </div>
      {installs.map((p) => <InstallWindowChip key={p.id} project={p} compact={compact} />)}
      {calls.map((c) => <MarketingCallChip key={c.id} call={c} compact={compact} isStaff={isStaff} />)}
      {blocks.map((b) => (
        <BlockChip key={b.id} block={b} project={projects.find((p) => p.id === b.project_id)} crew={crew} compact={compact} readOnly={readOnly} rainRisk={rainRisks.find((r) => r.block_id === b.id)} onEdit={onEdit} onShift={onShift} onDuplicate={onDuplicate} />
      ))}
    </div>
  );
}

export function CalendarGrid({ view, anchor, blocks, projects, crew, marketingCalls = [], isStaff = false, readOnly, weather = {}, rainRisks = [], onAdd, onEdit, onShift, onDuplicate }) {
  let days = [];
  if (view === 'week') {
    const s = startOfWeek(anchor);
    days = Array.from({ length: 7 }, (_, i) => addDays(s, i));
  } else {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const s = startOfWeek(first);
    const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    const n = Math.ceil((Math.round((last - s) / 86400000) + 1) / 7) * 7;
    days = Array.from({ length: n }, (_, i) => addDays(s, i));
  }
  const compact = view === 'month';
  return (
    <div className="border-t border-l border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm" data-testid={`calendar-${view}`}>
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        {DOW.map((d) => <div key={d} className="text-[11px] font-bold uppercase tracking-wider text-gray-500 py-2 text-center border-r border-gray-200">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const di = iso(d);
          return (
            <DayCell key={di} dayIso={di} inMonth={view === 'week' || d.getMonth() === anchor.getMonth()}
              blocks={blocks.filter((b) => coversDay(b, di))}
              installs={projects.filter((p) => p.install_date && p.install_date <= di && (p.install_end || p.install_date) >= di)}
              calls={marketingCalls.filter((c) => c.date === di)} isStaff={isStaff}
              projects={projects} crew={crew} readOnly={readOnly} compact={compact} weather={weather[di]} rainRisks={rainRisks}
              onAdd={onAdd} onEdit={onEdit} onShift={onShift} onDuplicate={onDuplicate} />
          );
        })}
      </div>
    </div>
  );
}
